import {
  LuUser,
  LuBell,
  LuBuilding2,
  LuUsers,
  LuMapPin,
  LuFileText,
  LuPackage,
  LuFileBox,
  LuTags,
  LuBoxes,
  LuShield,
  LuFileCheck,
  LuCode,
  LuCreditCard,
  LuFile,
  LuWorkflow,
  LuCalendarDays,
} from "react-icons/lu";

export const SETTINGS_SHORTCUT_ITEMS = [
  { key: "profile", title: "Profile", icon: LuUser },
  { key: "practiceprofile", title: "Practices", icon: LuBuilding2 },
  { key: "providers", title: "Providers", icon: LuUsers },
  { key: "locations", title: "Practice Locations", icon: LuMapPin },
  { key: "contracts", title: "Contracts", icon: LuFileText },
  { key: "productandservices", title: "Product and Services", icon: LuPackage },
  { key: "documents", title: "Documents", icon: LuFileBox },
  { key: "tags", title: "Tags", icon: LuTags },
  { key: "integrations", title: "Integrations", icon: LuBoxes },
  { key: "usermanagement", title: "User Management", icon: LuShield },
  { key: "submission", title: "Submission", icon: LuFileCheck },
  { key: "coding", title: "Coding", icon: LuCode },
  { key: "insuranceedi", title: "Insurance / EDI Codes", icon: LuCreditCard },
  { key: "patientstatements", title: "Patient Statements", icon: LuFile },
  { key: "rbs", title: "Rule Based System", icon: LuWorkflow },
  { key: "tiappointmenttypes", title: "Appointment Types", icon: LuCalendarDays },
  { key: "ticommunications", title: "Communications", icon: LuBell },
];

export const SETTINGS_SHORTCUTS_BY_KEY = SETTINGS_SHORTCUT_ITEMS.reduce(
  (acc, item) => {
    acc[item.key] = item;
    return acc;
  },
  {},
);

const STORAGE_PREFIX = "tc-settings-shortcuts-v1";
const STORAGE_ORDER_SUFFIX = ":order";

const getUserIdentity = (userinfo) => {
  if (!userinfo) return null;
  return (
    userinfo.userID ??
    userinfo.userId ??
    userinfo.id ??
    userinfo.userEmail ??
    null
  );
};

export const getSettingsShortcutStorageKey = (userinfo) => {
  const identity = getUserIdentity(userinfo);
  return identity ? `${STORAGE_PREFIX}:${identity}` : STORAGE_PREFIX;
};

const sanitizeShortcutKeys = (keys) => {
  if (!Array.isArray(keys)) return [];
  const known = new Set(SETTINGS_SHORTCUT_ITEMS.map((item) => item.key));
  return [...new Set(keys)].filter((key) => known.has(key));
};

const getDefaultShortcutOrder = () =>
  SETTINGS_SHORTCUT_ITEMS.map((item) => item.key);

export const readPinnedSettingsShortcuts = (userinfo) => {
  try {
    const storageKey = getSettingsShortcutStorageKey(userinfo);
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return [];
    return sanitizeShortcutKeys(JSON.parse(raw));
  } catch (error) {
    return [];
  }
};

export const writePinnedSettingsShortcuts = (userinfo, shortcutKeys) => {
  try {
    const storageKey = getSettingsShortcutStorageKey(userinfo);
    const sanitized = sanitizeShortcutKeys(shortcutKeys);
    window.localStorage.setItem(storageKey, JSON.stringify(sanitized));
    return sanitized;
  } catch (error) {
    return sanitizeShortcutKeys(shortcutKeys);
  }
};

export const readSettingsShortcutOrder = (userinfo) => {
  const defaultOrder = getDefaultShortcutOrder();
  try {
    const storageKey = `${getSettingsShortcutStorageKey(userinfo)}${STORAGE_ORDER_SUFFIX}`;
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      const pinned = readPinnedSettingsShortcuts(userinfo);
      const pinnedSet = new Set(pinned);
      const rest = defaultOrder.filter((key) => !pinnedSet.has(key));
      return [...pinned, ...rest];
    }
    const stored = sanitizeShortcutKeys(JSON.parse(raw));
    const storedSet = new Set(stored);
    const missing = defaultOrder.filter((key) => !storedSet.has(key));
    return [...stored, ...missing];
  } catch (error) {
    return defaultOrder;
  }
};

export const writeSettingsShortcutOrder = (userinfo, orderKeys) => {
  const defaultOrder = getDefaultShortcutOrder();
  try {
    const storageKey = `${getSettingsShortcutStorageKey(userinfo)}${STORAGE_ORDER_SUFFIX}`;
    const sanitized = sanitizeShortcutKeys(orderKeys);
    const sanitizedSet = new Set(sanitized);
    const withMissing = [...sanitized, ...defaultOrder.filter((key) => !sanitizedSet.has(key))];
    window.localStorage.setItem(storageKey, JSON.stringify(withMissing));
    return withMissing;
  } catch (error) {
    const fallback = sanitizeShortcutKeys(orderKeys);
    const fallbackSet = new Set(fallback);
    return [...fallback, ...defaultOrder.filter((key) => !fallbackSet.has(key))];
  }
};

